import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_FILTERS = ["all", "open", "pending", "closed"];

export default function AdminTickets() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.is_admin === false) nav("/dashboard");
  }, [user, nav]);

  const load = async () => {
    setError("");
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/admin/support/tickets${params}`);
      setTickets(r.data || []);
      if (active) {
        const updated = (r.data || []).find(t => t.id === active.id);
        if (updated) setActive(updated);
      }
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${active.id}/reply`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const [note, setNote] = useState("");
  const decideRelease = async (action) => {
    if (!window.confirm(action === "approve" ? `Approve and move $${active.release.amount} from ${active.release.vault_name} to this user's Spending?` : "Deny this early release?")) return;
    setBusy(true); setError("");
    try {
      await api.post(`/admin/support/tickets/${active.id}/release`, { action, note: note.trim() });
      setNote("");
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/support/tickets/${active.id}/status`, { status });
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <button onClick={() => nav("/admin")}
          className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-ink mb-4"
          data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Admin · Support</div>
        <h1 className="font-serif text-4xl tracking-tight mt-2">Tickets queue</h1>

        <div className="mt-6 flex gap-2" data-testid="admin-ticket-filters">
          {STATUS_FILTERS.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`text-xs px-3 py-1.5 rounded-lg border ${
                filter === s
                  ? "bg-forest text-white border-forest"
                  : "bg-white text-moss border-sand"
              }`}>
              {s}
            </button>
          ))}
        </div>

        {error && <div className="mt-6 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg">{error}</div>}

        <div className="mt-8 grid md:grid-cols-[1fr_1.4fr] gap-6">
          <div className="space-y-2" data-testid="admin-tickets-list">
            {tickets.length === 0 && (
              <div className="text-sm text-sage italic bg-linen border border-sand rounded-lg p-6 text-center">No tickets in this view.</div>
            )}
            {tickets.map(t => (
              <button key={t.id} onClick={() => setActive(t)}
                data-testid={`admin-ticket-row-${t.id}`}
                className={`w-full text-left rounded-xl p-4 border transition-colors ${
                  active?.id === t.id
                    ? "bg-forest text-white border-forest"
                    : "bg-linen border-sand hover:border-forest"
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-serif truncate">{t.subject}</h3>
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded shrink-0 ${
                    active?.id === t.id ? "bg-white/15" : "bg-sand-light text-moss"
                  }`}>{t.status}</span>
                </div>
                <div className={`text-xs mt-1 truncate ${active?.id === t.id ? "text-sage-mid" : "text-moss"}`}>
                  {t.user_email}
                </div>
                <div className={`text-[11px] mt-1 ${active?.id === t.id ? "text-sage-mid" : "text-sage"}`}>
                  {new Date(t.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>

          <div data-testid="admin-ticket-detail">
            {!active && (
              <div className="bg-linen border border-sand rounded-xl p-12 text-center">
                <MessageSquare className="w-8 h-8 text-forest mx-auto" strokeWidth={1.5} />
                <p className="text-sm text-sage mt-3">Select a ticket to view and reply.</p>
              </div>
            )}
            {active && (
              <div>
                <h2 className="font-serif text-2xl">{active.subject}</h2>
                <p className="text-xs text-sage mt-1">{active.user_email} · {active.user_name}</p>

                {active.kind === "early_release" && active.release && (
                  <div className="mt-4 rounded-xl border border-gold/50 bg-gold/10 p-4" data-testid="admin-release-panel">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gold-deep">Early release request · 24h SLA</div>
                    <div className="mt-1 text-sm text-ink"><strong>${Number(active.release.amount).toFixed(2)}</strong> from <strong>{active.release.vault_name}</strong> · status <span className="uppercase font-semibold" data-testid="admin-release-status">{active.release.status}</span></div>
                    <div className="text-[11px] text-moss mt-1">Approve only if it fits the member&apos;s Level Up career goals. Approving moves the money to their Spending automatically.</div>
                    {active.release.status === "requested" && (
                      <div className="mt-3 space-y-2">
                        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note to the member" maxLength={4000}
                          className="w-full bg-white border border-sand rounded-md px-3 py-2 text-sm" data-testid="admin-release-note" />
                        <div className="flex gap-2">
                          <button onClick={() => decideRelease("approve")} disabled={busy} className="bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60" data-testid="admin-release-approve">Approve & move money</button>
                          <button onClick={() => decideRelease("deny")} disabled={busy} className="border border-clay text-clay rounded-md px-4 py-2 text-xs font-medium hover:bg-clay hover:text-white disabled:opacity-60" data-testid="admin-release-deny">Deny</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-4 bg-linen border border-sand rounded-xl p-4 text-sm whitespace-pre-wrap" data-testid="admin-ticket-body">
                  {active.body}
                </div>

                <div className="mt-4 space-y-3" data-testid="admin-ticket-messages">
                  {(active.messages || []).map((m, i) => (
                    <div key={i} className={`rounded-xl p-3 text-sm whitespace-pre-wrap ${
                      m.is_admin
                        ? "bg-forest text-cream ml-8"
                        : "bg-white border border-sand mr-8"
                    }`}>
                      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 ${m.is_admin ? "text-sage-mid" : "text-sage"}`}>
                        {m.is_admin ? "You (admin)" : active.user_email} · {new Date(m.created_at).toLocaleString()}
                      </div>
                      {m.body}
                    </div>
                  ))}
                </div>

                {active.status !== "closed" && (
                  <div className="mt-4 bg-linen border border-sand rounded-xl p-3">
                    <textarea value={reply} onChange={e => setReply(e.target.value)}
                      placeholder="Write a reply…" rows={3} maxLength={10000}
                      data-testid="admin-ticket-reply-input"
                      className="w-full bg-white border border-sand rounded-md px-3 py-2 text-sm resize-none" />
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <div className="flex gap-1.5">
                        <button onClick={() => setStatus("closed")} disabled={busy}
                          data-testid="admin-close-ticket-btn"
                          className="text-xs px-3 py-1.5 rounded-md border border-sand text-moss hover:text-clay">Close</button>
                      </div>
                      <button onClick={sendReply} disabled={busy || !reply.trim()}
                        data-testid="admin-ticket-reply-btn"
                        className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60">
                        <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send reply"}
                      </button>
                    </div>
                  </div>
                )}
                {active.status === "closed" && (
                  <button onClick={() => setStatus("open")} disabled={busy}
                    className="mt-4 text-xs px-3 py-1.5 rounded-md border border-sand text-moss hover:text-forest"
                    data-testid="admin-reopen-ticket-btn">
                    Reopen ticket
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
